
import React from 'react';
import { Order, OrderStatus, Language } from '../types';
import { Box, Clock, CheckCircle2, XCircle, Calendar, Hash, Tag, Flower, ExternalLink } from 'lucide-react';

interface UserOrdersProps {
  orders: Order[];
  language: Language;
}

const UserOrders: React.FC<UserOrdersProps> = ({ orders, language }) => {
  const t = {
    pt: {
      title: 'Santuário de Pedidos',
      sub: 'Suas aquisições seladas no acervo Niiko',
      noOrders: 'Nenhuma obra selada ainda.',
      status: 'Status',
      total: 'Investimento Total',
      date: 'Data do Selo',
      id: 'ID do Pedido',
      viewArt: 'Ver Arte',
      pending: 'Em Meditação',
      completed: 'Selado com Sucesso',
      cancelled: 'Vínculo Quebrado'
    },
    en: {
      title: 'Order Sanctuary',
      sub: 'Your acquisitions sealed in the Niiko collection',
      noOrders: 'No works sealed yet.',
      status: 'Status',
      total: 'Total Investment',
      date: 'Sealing Date',
      id: 'Order ID',
      viewArt: 'View Art',
      pending: 'Meditating',
      completed: 'Successfully Sealed',
      cancelled: 'Bond Broken'
    }
  }[language];

  const getStatusInfo = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.COMPLETED:
        return { 
          icon: <CheckCircle2 className="w-4 h-4" />, 
          text: t.completed, 
          class: 'bg-green-500/10 text-green-500 border-green-500/20' 
        };
      case OrderStatus.CANCELLED:
        return { 
          icon: <XCircle className="w-4 h-4" />, 
          text: t.cancelled, 
          class: 'bg-red-500/10 text-red-500 border-red-500/20' 
        };
      default:
        return { 
          icon: <Clock className="w-4 h-4" />, 
          text: t.pending, 
          class: 'bg-[#c084fc]/10 text-[#c084fc] border-[#c084fc]/20 shadow-[0_0_10px_rgba(192,132,252,0.1)]' 
        };
    }
  };

  const formatPrice = (price: number) => {
    if (language === 'en') return `$ ${price.toFixed(2)}`;
    return `R$ ${(price * 5).toFixed(2)}`;
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-8 duration-1000">
      <div className="flex items-center gap-6 mb-16">
        <div className="bg-white p-4 shadow-2xl border-b-4 border-[#c084fc]">
          <Box className="w-8 h-8 text-[#c084fc]" />
        </div>
        <div>
          <h2 className="text-4xl font-black text-white uppercase tracking-tighter oil-text">{t.title}</h2>
          <p className="text-zinc-500 text-xs font-black uppercase tracking-[0.4em]">{t.sub}</p>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="bg-zinc-900/40 border border-white/5 p-20 text-center backdrop-blur-xl">
           <Flower className="w-16 h-16 text-zinc-800 mx-auto mb-6 opacity-20" />
           <p className="text-zinc-600 font-black uppercase tracking-widest text-sm">{t.noOrders}</p>
        </div>
      ) : (
        <div className="space-y-8">
          {orders.map((order) => {
            const status = getStatusInfo(order.status);
            return (
              <div key={order.id} className="bg-zinc-900/40 border border-white/5 backdrop-blur-xl overflow-hidden group hover:border-[#c084fc]/30 transition-all shadow-2xl">
                <div className="p-8 border-b border-white/5 flex flex-wrap justify-between items-center gap-6 bg-zinc-950/30">
                  <div className="flex flex-wrap gap-8">
                    <div className="flex flex-col">
                      <span className="text-[9px] font-black text-zinc-600 uppercase tracking-widest mb-1 flex items-center gap-2">
                        <Hash className="w-3 h-3" /> {t.id}
                      </span>
                      <span className="text-white font-mono text-xs font-bold">{order.id}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[9px] font-black text-zinc-600 uppercase tracking-widest mb-1 flex items-center gap-2">
                        <Calendar className="w-3 h-3" /> {t.date}
                      </span>
                      <span className="text-white font-mono text-xs font-bold">{order.date}</span>
                    </div>
                  </div>
                  <div className={`flex items-center gap-3 px-6 py-2 border font-black text-[10px] uppercase tracking-widest ${status.class}`}>
                    {status.icon}
                    {status.text}
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  {order.items.map((item, idx) => (
                    <div key={`${item.id}-${idx}`} className="flex items-center gap-6 group/item">
                      <div className="w-20 h-20 bg-black border border-white/5 overflow-hidden flex-shrink-0">
                        {item.videoUrl ? (
                          <video src={item.videoUrl} autoPlay loop muted playsInline className="w-full h-full object-cover opacity-80 group-hover/item:opacity-100 transition-opacity hfr-media" />
                        ) : (
                          <img src={item.imageUrl} alt={item.title[language]} className="w-full h-full object-cover opacity-80 group-hover/item:opacity-100 transition-opacity" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-black uppercase text-sm tracking-tight mb-1 group-hover/item:text-[#c084fc] transition-colors">{item.title[language]}</h4>
                        <div className="flex flex-wrap gap-4 items-center">
                          <span className="text-[9px] text-zinc-500 font-bold uppercase tracking-widest bg-white/5 px-2 py-0.5">{item.category}</span>
                          {item.customSignature && (
                            <span className="text-[9px] text-[#c084fc] font-black uppercase tracking-widest artistic-font">Sig: {item.customSignature}</span>
                          )}
                          {item.dafontLink && (
                            <a href={item.dafontLink} target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-white transition-colors flex items-center gap-1 text-[8px] font-bold uppercase underline">
                              <ExternalLink className="w-2.5 h-2.5" /> Font
                            </a>
                          )}
                        </div>
                        {item.notes && (
                          <p className="text-[10px] text-zinc-400 mt-2 border-l border-[#c084fc]/30 pl-2 py-0.5 italic">{language === 'en' ? 'Notes:' : 'Obs:'} {item.notes}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-white font-mono font-bold text-sm">{formatPrice(item.price)}</p>
                        <p className="text-zinc-600 text-[9px] font-black uppercase">qty: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-8 bg-black/40 border-t border-white/5 flex justify-between items-center">
                  <span className="text-zinc-500 font-black text-[10px] uppercase tracking-[0.4em]">{t.total}</span>
                  <span className="text-[#c084fc] font-mono font-black text-2xl tracking-tighter shadow-lg">{formatPrice(order.total)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserOrders;
